/*
  <desk-height-slider> — interactive sit<->stand desk demo (S6).

  Self-contained: no product, cart, variant or pubsub coupling. Reads its
  configuration from data-* attributes (min/max/step/presets/labels) so the
  module is reusable across any desk template. rAF-throttled, prefers-
  reduced-motion aware, keyboard-native via the underlying <input type=range>.

    legHeightPx = 24 + (val - min) / (max - min) * 78   (clamped to design 600..1250 span = 650)
    state label: val < lowThreshold -> sit, val > highThreshold -> stand, else adjusting
*/
if (!customElements.get('desk-height-slider')) {
  customElements.define(
    'desk-height-slider',
    class DeskHeightSlider extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-dhs-slider]');
        this.legs = this.querySelectorAll('[data-dhs-leg]');
        this.cmEl = this.querySelector('[data-dhs-cm]');
        this.posEl = this.querySelector('[data-dhs-pos]');
        this.liveEl = this.querySelector('[data-dhs-live]');
        this.presets = this.querySelectorAll('[data-dhs-preset]');

        if (!this.slider) return;

        // Span the design's leg formula is calibrated to (600..1250 => 650mm).
        // Even if the merchant narrows min/max, leg travel stays proportional
        // to the configured range so the desk never overflows its card.
        this.min = Number(this.slider.min) || 600;
        this.max = Number(this.slider.max) || 1250;
        this.span = this.max - this.min || 650;

        this.low = Number(this.dataset.lowThreshold) || 780;
        this.high = Number(this.dataset.highThreshold) || 1020;

        this.labelSit = this.dataset.labelSit || 'Poziție șezut';
        this.labelStand = this.dataset.labelStand || 'Poziție în picioare';
        this.labelMid = this.dataset.labelMid || 'Poziție intermediară';

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._frame = null;

        this.onInput = this.onInput.bind(this);
        this.onPreset = this.onPreset.bind(this);

        this.slider.addEventListener('input', this.onInput);
        this.presets.forEach((btn) => btn.addEventListener('click', this.onPreset));

        // Initial paint (synchronous so SSR value + DOM agree on first frame).
        this.render(Number(this.slider.value));
      }

      disconnectedCallback() {
        if (this._frame) cancelAnimationFrame(this._frame);
        if (this.slider) this.slider.removeEventListener('input', this.onInput);
        this.presets.forEach((btn) => btn.removeEventListener('click', this.onPreset));
      }

      onInput() {
        this.schedule(Number(this.slider.value));
      }

      onPreset(e) {
        const target = Number(e.currentTarget.dataset.dhsPreset);
        if (Number.isNaN(target)) return;
        const clamped = Math.min(this.max, Math.max(this.min, target));
        this.slider.value = clamped;
        // Native input event keeps the range as the single source of truth.
        this.schedule(clamped);
      }

      schedule(val) {
        if (this._frame) cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => {
          this._frame = null;
          this.render(val);
        });
      }

      stateFor(val) {
        if (val < this.low) return { label: this.labelSit, key: 'sit' };
        if (val > this.high) return { label: this.labelStand, key: 'stand' };
        return { label: this.labelMid, key: 'mid' };
      }

      render(val) {
        const legH = 24 + ((val - this.min) / this.span) * 78;
        this.legs.forEach((leg) => {
          leg.style.setProperty('--leg-h', legH.toFixed(1) + 'px');
        });

        const cm = Math.round(val / 10);
        const cmText = cm + ' cm';
        const state = this.stateFor(val);

        if (this.cmEl) this.cmEl.textContent = cmText;
        if (this.posEl) this.posEl.textContent = state.label;

        // aria-valuetext gives AT a human reading instead of raw "720".
        this.slider.setAttribute('aria-valuetext', cmText + ' · ' + state.label);

        // Mirror preset pressed-state to whichever preset matches the value.
        this.presets.forEach((btn) => {
          const pressed = Number(btn.dataset.dhsPreset) === val;
          btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        });

        // aria-live announcement (debounced naturally by rAF coalescing).
        if (this.liveEl) this.liveEl.textContent = cmText + ', ' + state.label;
      }
    }
  );
}
